import React from 'react';
import { cn } from '@/lib/utils';

interface CriticalErrorProps {
  title?: string;
  message?: string;
  code?: string;
  buttonText?: string;
  icon?: React.ReactNode;
  className?: string;
  /** Retry in place. Falls back to a full page reload when not provided —
   *  which is right for a failure that left the app unable to start, and
   *  wrong for one an error boundary can simply re-render past. */
  onRetry?: () => void;
}

export const CriticalError = ({
  title = 'System Error',
  message = 'An unexpected error occurred. The application cannot start.',
  code,
  buttonText = 'Retry Connection',
  icon,
  className,
  onRetry,
}: CriticalErrorProps) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center overflow-hidden',
        'bg-background p-6 text-center font-sans text-foreground',
        className,
      )}
    >
      <div
        className={cn(
          'max-w-md rounded-lg bg-card p-8 shadow-xl border border-destructive/20',
        )}
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          {icon ? (
            icon
          ) : (
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
        </div>

        <h1 className="mb-2 text-xl font-bold text-foreground">{title}</h1>

        <p className="mb-6 text-sm text-muted-foreground">{message}</p>

        {code && (
          <div className="rounded bg-muted p-3 mb-6">
            <p className="text-xs font-mono text-muted-foreground">
              Code: <span className="font-bold text-foreground">{code}</span>
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => { window.location.href = '/strategies'; }}
            className={cn(
              'flex-1 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground',
              'hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring',
              'focus:ring-offset-2',
            )}
          >
            Back to Dashboard
          </button>
          <button
            onClick={handleRetry}
            className={cn(
              'flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
              'hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring',
              'focus:ring-offset-2',
            )}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};
